import Constants from '@/Constants';
import { SlideConfig } from '@/models/SlideConfig';
import { getViewTypeData, ViewType } from '@/models/ViewType';
import EssentialHeader from '@/pages/Student/Home/EssentialHeader';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ContentSection from '../ContentSection';
import CourseSection from '../CourseSection';
import Loader from '../Loader';
import ProgramSection from '../ProgramSection';
import './style.scss';

interface Props {
    viewType: ViewType;
    simplified?: boolean;
    dynamicTitle?: string;
    courseSlideConfiguration?: SlideConfig;
    hideTitle?: boolean;
    randomizeRecommendations?: boolean;
    localesToFilter?: string;
}

interface DefaultHeaderProps {
    showHeader: boolean;
    title?: string;
}

const StudentViewSection: React.FC<Props> = (props: Props) => {
    const { viewType, simplified, dynamicTitle, courseSlideConfiguration: courseSlideConfig, hideTitle = false, localesToFilter } = props;
    const { total: courseListSizeTotal, loading: courseLoading } = useSelector((state: ApplicationState) => state.lists.listData[viewType]);
    const { total: contentListSizeTotal, loading: contentLoading } = useSelector((state: ApplicationState) => state.itens.listData[viewType]);
    const { total: programListSizeTotal, loading: programLoading } = useSelector(
        (state: ApplicationState) => state.program.listData[viewType] || { total: 0, loading: false },
    );

    const [showSection, setShowSection] = useState(true);

    const viewTypeData = getViewTypeData(viewType);

    const viewTypesWithoutLoading: ViewType[] = ['listMyCourseStudentView', 'listEssentialStudentView'];

    const getContentSimplifiedLimit = () => {
        return courseListSizeTotal === 0 ? Constants.SIMPLIFIED_CONTENT_LIMIT_WITHOUT_COURSE : Constants.SIMPLIFIED_CONTENT_LIMIT_WITH_COURSE;
    };

    const getHeader = (): React.ReactNode => {
        switch (viewType) {
            case 'listEssentialStudentView':
                return <EssentialHeader />;
            default:
                return (
                    <DefaultHeader
                        showHeader={(contentListSizeTotal > 0 || courseListSizeTotal > 0 || programListSizeTotal > 0) && !hideTitle}
                        title={dynamicTitle || viewTypeData?.title}
                    />
                );
        }
    };

    useEffect(() => {
        if (
            !courseLoading &&
            !contentLoading &&
            !programLoading &&
            courseListSizeTotal === 0 &&
            contentListSizeTotal === 0 &&
            programListSizeTotal === 0
        ) {
            setShowSection(false);
        } else {
            setShowSection(true);
        }
    }, [courseListSizeTotal, contentListSizeTotal, programListSizeTotal, courseLoading, contentLoading, programLoading]);

    return (
        <>
            {showSection && (
                <div className={`dynamic-groupers ${contentListSizeTotal === 0 ? 'no-content' : ''}`}>
                    <div className="dynamic-groupers__container container">
                        {getHeader()}

                        <ProgramSection key={`program_section_${viewType}`} localesToFilter={localesToFilter} mode={'view'} viewType={viewType} />

                        <CourseSection
                            key={`course_section_${viewType}`}
                            localesToFilter={localesToFilter}
                            mode={'view'}
                            slideConfiguration={courseSlideConfig}
                            viewType={viewType}
                        />
                        {!courseLoading && (
                            <ContentSection
                                key={`content_section_${viewType}`}
                                limit={simplified ? getContentSimplifiedLimit() : Constants.CONTENT_LIMIT}
                                localesToFilter={localesToFilter}
                                randomizeRecommendations={props.randomizeRecommendations}
                                simplified={!!simplified}
                                viewType={viewType}
                            />
                        )}
                        {simplified && contentListSizeTotal > getContentSimplifiedLimit() && (
                            <div className="row justify-content-center">
                                <Link className="dynamic-groupers__link-more" to={`/${Constants.SECTIONS_RESOURCE_NAME}/${viewTypeData.resource}`}>
                                    + {viewTypeData.title}
                                </Link>
                            </div>
                        )}
                        {(courseLoading || contentLoading || programLoading) && !viewTypesWithoutLoading.includes(viewType) && <Loader />}
                    </div>
                </div>
            )}
        </>
    );
};

const DefaultHeader: React.FC<DefaultHeaderProps> = ({ showHeader, title }: DefaultHeaderProps) => (
    <>{showHeader && <h2 className="dynamic-groupers__title">{title}</h2>}</>
);

export default StudentViewSection;
